import * as React from "react"
import { Check, ChevronsUpDown, Plus } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

/**
 * SearchableSelectWithAction - A combobox with optional "+ Create New" action
 * 
 * Usage:
 * <SearchableSelectWithAction
 *   options={[{ value: "ABC", label: "ABC - Product Name", displayValue: "ABC" }]}
 *   value={selectedValue}
 *   onValueChange={setSelectedValue}
 *   placeholder="Select or type..."
 *   onCreateNew={() => openDialog()}
 *   createNewLabel="+ Create New Code"
 * />
 */
export function SearchableSelectWithAction({
  options = [],
  value,
  onValueChange,
  placeholder = "Select...",
  searchPlaceholder = "Type to search...",
  emptyMessage = "No results found.",
  disabled = false,
  className,
  triggerClassName,
  onCreateNew,
  createNewLabel = "+ Create New",
  "data-testid": dataTestId,
}) {
  const [open, setOpen] = React.useState(false)
  const [searchValue, setSearchValue] = React.useState("")
  
  const selectedOption = options.find((opt) => opt.value === value)
  
  // Filter options based on search
  const filteredOptions = React.useMemo(() => {
    if (!searchValue) return options
    const search = searchValue.toLowerCase()
    return options.filter(opt => 
      opt.label.toLowerCase().includes(search) ||
      opt.value.toLowerCase().includes(search)
    )
  }, [options, searchValue])

  return (
    <Popover open={open} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full justify-between font-normal h-9",
            !value && "text-muted-foreground",
            triggerClassName
          )}
          data-testid={dataTestId}
        >
          <span className="truncate font-mono text-sm">
            {selectedOption ? (selectedOption.displayValue || selectedOption.value) : placeholder}
          </span>
          <ChevronsUpDown className="ml-1 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn("w-[280px] p-0", className)} align="start">
        <Command shouldFilter={false}>
          <CommandInput 
            placeholder={searchPlaceholder} 
            value={searchValue}
            onValueChange={setSearchValue}
          />
          <CommandList>
            {filteredOptions.length === 0 && !onCreateNew && (
              <CommandEmpty>{emptyMessage}</CommandEmpty>
            )}
            {filteredOptions.length === 0 && onCreateNew && (
              <CommandEmpty className="py-2 text-center text-sm text-muted-foreground">
                No matching codes found
              </CommandEmpty>
            )}
            <CommandGroup>
              {filteredOptions.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  onSelect={() => {
                    onValueChange(option.value === value ? "" : option.value)
                    setOpen(false)
                    setSearchValue("")
                  }}
                  className="cursor-pointer"
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4 flex-shrink-0",
                      value === option.value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <span className="truncate">{option.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
            {onCreateNew && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    onSelect={() => {
                      setOpen(false)
                      setSearchValue("")
                      onCreateNew(searchValue)
                    }}
                    className="cursor-pointer text-blue-600 hover:text-blue-700"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    {createNewLabel}
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export default SearchableSelectWithAction
